import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import AnimatedCounter from "./AnimatedCounter";

interface TotalEventsSlideProps {
  totalEvents: number;
  monthlyActivity: Record<string, number>;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const TotalEventsSlide = ({ totalEvents, monthlyActivity }: TotalEventsSlideProps) => {
  const maxValue = Math.max(...Object.values(monthlyActivity), 1);

  return (
    <div className="flex flex-col items-center justify-center text-center px-6 w-full max-w-lg">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="mb-6"
      >
        <Calendar className="w-16 h-16 text-secondary" />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-background/80 mb-2"
      >
        You attended
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, type: "spring" }}
      >
        <AnimatedCounter
          value={totalEvents}
          className="text-7xl md:text-8xl font-bold text-secondary"
        />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-2xl text-background mt-2"
      >
        {totalEvents === 1 ? "event" : "events"}
      </motion.p>

      {/* Monthly chart */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mt-10 w-full"
      >
        <div className="flex items-end justify-between gap-1 h-24">
          {MONTHS.map((month, idx) => {
            const value = monthlyActivity[month] || 0;
            const MAX_BAR_HEIGHT = 64;
            const barHeight = value > 0 ? Math.max(12, (value / maxValue) * MAX_BAR_HEIGHT) : 4;

            return (
              <motion.div
                key={month}
                className="flex-1 flex flex-col items-center gap-1"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: 1 + idx * 0.05, duration: 0.3 }}
                style={{ originY: 1 }}
              >
                <div
                  className={`w-full rounded-t ${
                    value > 0 ? "bg-secondary" : "bg-background/20"
                  }`}
                  style={{ height: `${barHeight}px` }}
                />
                <span className="text-[10px] text-background/60">{month}</span>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default TotalEventsSlide;
